import { Trans, useTranslation } from 'react-i18next';
import type { Pagination as PaginationMeta } from '@/lib/api/types';
import { Button } from './Button';
import { MonoValue } from './Typography';
import { DEFAULT_PER_PAGE } from '@/lib/config';

const PER_PAGE_OPTIONS = [DEFAULT_PER_PAGE, 50, 100];

export function Pagination({
  meta,
  onPageChange,
  onPerPageChange,
}: {
  meta: PaginationMeta;
  onPageChange: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
}) {
  const { t } = useTranslation();
  const { page, per_page, total, total_pages } = meta;
  const from = total === 0 ? 0 : (page - 1) * per_page + 1;
  const to = Math.min(page * per_page, total);
  return (
    <div className="mt-md flex flex-wrap items-center justify-between gap-md">
      <p className="text-small text-ink-mute">
        <Trans
          i18nKey="ui.paginationSummary"
          values={{ from, to, total }}
          components={{
            from: <MonoValue>{0}</MonoValue>,
            to: <MonoValue>{0}</MonoValue>,
            total: <MonoValue>{0}</MonoValue>,
          }}
        />
      </p>
      <div className="flex items-center gap-sm">
        {onPerPageChange && (
          <label className="flex items-center gap-xs text-label uppercase tracking-[0.1em] text-ink-mute">
            {t('ui.perPage')}
            <select
              value={per_page}
              onChange={(e) => onPerPageChange(Number(e.target.value))}
              className="rounded-sm border-[0.5px] border-hairline bg-canvas-sand px-xs py-[4px] font-mono text-small"
            >
              {PER_PAGE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )}
        <Button
          variant="ghost"
          size="sm"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label={t('ui.previousPage')}
        >
          ‹ {t('ui.prev')}
        </Button>
        <span className="text-small text-ink-mid">
          <MonoValue>{page}</MonoValue> / <MonoValue>{total_pages}</MonoValue>
        </span>
        <Button
          variant="ghost"
          size="sm"
          disabled={page >= total_pages}
          onClick={() => onPageChange(page + 1)}
          aria-label={t('ui.nextPage')}
        >
          {t('ui.next')} ›
        </Button>
      </div>
    </div>
  );
}
